import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { BUSINESS, NAV } from "@/data/site";

const Logo = ({ onClick }) => (
  <Link to="/" onClick={onClick} data-testid="nav-logo" className="flex items-center gap-3 shrink-0">
    <img src={BUSINESS.logo} alt="A.D.N Stucco Inc logo" className="h-12 w-auto object-contain" />
    <div className="leading-none hidden sm:block">
      <div className="font-heading font-extrabold text-lg tracking-tight">
        <span className="text-adn-red">A.D.N</span> <span className="text-adn-charcoal">STUCCO INC</span>
      </div>
      <div className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase mt-0.5">Central Florida</div>
    </div>
  </Link>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${scrolled || mobileOpen ? "bg-white border-b border-zinc-200" : "bg-white/80 backdrop-blur-md border-b border-transparent"}`} data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-1">
          {NAV.filter(n => n.label !== "Contact").map((item) => (
            item.children ? (
              <div key={item.label} className="relative group">
                <Link to={item.to} data-testid={`nav-${item.label.toLowerCase()}`} className="flex items-center gap-1 px-3 py-2 text-sm font-semibold text-adn-charcoal hover:text-adn-red transition-colors">
                  {item.label} <ChevronDown size={14} />
                </Link>
                <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="bg-white border border-zinc-200 shadow-xl min-w-[240px] py-2">
                    {item.children.map((c) => (
                      <Link key={c.to} to={c.to} data-testid={`nav-sub-${c.to}`} className="block px-5 py-2.5 text-sm text-zinc-700 hover:bg-adn-off hover:text-adn-red transition-colors">{c.label}</Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link key={item.label} to={item.to} data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`} className="px-3 py-2 text-sm font-semibold text-adn-charcoal hover:text-adn-red transition-colors">{item.label}</Link>
            )
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-4">
          <a href={BUSINESS.phoneHref} data-testid="nav-phone" className="flex items-center gap-2 text-sm font-bold text-adn-charcoal hover:text-adn-red transition-colors">
            <Phone size={16} className="text-adn-red" /> {BUSINESS.phone}
          </a>
          <Link to="/contact" data-testid="nav-cta" className="bg-adn-red text-white px-5 py-3 text-sm font-semibold hover:bg-adn-charcoal transition-colors">Free Estimate</Link>
        </div>
        <button className="lg:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle" aria-label="Toggle menu">
          {mobileOpen ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-zinc-200 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col">
            {NAV.map((item) => (
              <div key={item.label}>
                {item.children ? (
                  <>
                    <button onClick={() => setSvcOpen(!svcOpen)} className="w-full flex items-center justify-between py-3 font-semibold text-adn-charcoal" data-testid="mobile-services-toggle">
                      {item.label} <ChevronDown size={16} className={`transition-transform ${svcOpen ? "rotate-180" : ""}`} />
                    </button>
                    {svcOpen && item.children.map((c) => (
                      <Link key={c.to} to={c.to} className="block py-2 pl-4 text-sm text-zinc-600 hover:text-adn-red">{c.label}</Link>
                    ))}
                  </>
                ) : (
                  <Link to={item.to} className="block py-3 font-semibold text-adn-charcoal hover:text-adn-red">{item.label}</Link>
                )}
              </div>
            ))}
            <a href={BUSINESS.phoneHref} className="mt-3 flex items-center gap-2 py-3 font-bold text-adn-red"><Phone size={16} /> {BUSINESS.phone}</a>
            <Link to="/contact" className="mt-2 bg-adn-red text-white text-center px-5 py-3 font-semibold">Request a Free Estimate</Link>
          </div>
        </div>
      )}
    </header>
  );
}
